/**
 * LN2E envelope detection, extraction, and construction for FlatBuffer messages.
 *
 * The cloud server wraps outbound FlatBuffer payloads in a self-describing
 * 42-byte envelope so receivers can verify schema identity via a SHA-256
 * fingerprint of the .bfbs binary schema.
 *
 * Envelope format (42-byte header followed by the FlatBuffer payload):
 *
 *   [4 bytes]  magic: "LN2E" (0x4C 0x4E 0x32 0x45)
 *   [1 byte]   version: 0x01
 *   [1 byte]   hash algorithm: 0x01 = SHA-256
 *   [32 bytes] SHA-256 hash of the .bfbs binary schema
 *   [4 bytes]  payload length, little-endian uint32
 *   [N bytes]  FlatBuffer payload
 *
 * Used by both WebSocket (monitoring) and HTTP REST API (flatBufferBaseQuery).
 *
 * For backward compatibility the frontend uses `unwrapEnvelope`, which strips
 * the header without verifying the schema hash.  Raw (non-enveloped) messages
 * pass through unchanged, so old and new cloud servers are both supported.
 */
import { tracePointEmit } from "../integrationTrace";
import { TracePointId } from "./generated/integration-trace/trace-point-id";

const ENVELOPE_MAGIC_0 = 0x4c; // 'L'
const ENVELOPE_MAGIC_1 = 0x4e; // 'N'
const ENVELOPE_MAGIC_2 = 0x32; // '2'
const ENVELOPE_MAGIC_3 = 0x45; // 'E'

/** Total size of the fixed envelope header in bytes. */
export const ENVELOPE_HEADER_SIZE = 42;

/**
 * Returns `true` if `data` begins with the LN2E magic bytes and is at least
 * long enough to contain a complete envelope header.
 */
export function isEnvelope(data: Uint8Array): boolean {
  return (
    data.length >= ENVELOPE_HEADER_SIZE &&
    data[0] === ENVELOPE_MAGIC_0 &&
    data[1] === ENVELOPE_MAGIC_1 &&
    data[2] === ENVELOPE_MAGIC_2 &&
    data[3] === ENVELOPE_MAGIC_3
  );
}

/**
 * Extracts the FlatBuffer payload from an LN2E envelope without validating
 * the embedded schema hash.  Returns `null` if the header is malformed or
 * the declared payload length exceeds the available bytes.
 */
export function extractEnvelopePayload(data: Uint8Array): Uint8Array | null {
  if (!isEnvelope(data)) {
    return null;
  }
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const payloadLen = view.getUint32(38, /* littleEndian= */ true);
  const end = ENVELOPE_HEADER_SIZE + payloadLen;
  if (end > data.length) {
    return null;
  }
  return data.subarray(ENVELOPE_HEADER_SIZE, end);
}

/**
 * Strips an LN2E envelope from `data` if present, returning the inner
 * FlatBuffer payload.  If `data` is not an envelope (i.e. a raw FlatBuffer
 * from an older server), it is returned unchanged.
 *
 * This is the primary entry point for WebSocket message handling: call it
 * once on the raw `Uint8Array` before passing bytes to any decoder.
 */
export function unwrapEnvelope(data: Uint8Array): Uint8Array {
  if (!isEnvelope(data)) {
    return data;
  }
  // TP 404: FrontendEnvelopeStripped — an LN2E envelope was detected and stripped
  tracePointEmit(TracePointId.FrontendEnvelopeStripped);
  return extractEnvelopePayload(data) ?? data;
}

/** Result of extracting an envelope with its schema hash for type dispatch. */
export interface EnvelopeContents {
  /** The raw FlatBuffer payload bytes (after the 42-byte header). */
  payload: Uint8Array;
  /** The 32-byte SHA-256 hash of the .bfbs schema, hex-encoded. */
  schemaHash: string;
}

/**
 * Extracts both the FlatBuffer payload AND the schema hash from an LN2E
 * envelope.  Returns `null` if `data` is not a valid envelope.
 *
 * The schema hash is returned as a lowercase hex string (64 chars) so it
 * can be used directly as a key in the schema registry for type dispatch.
 *
 * Used by `flatBufferBaseQuery` to determine which `*T` type to unpack
 * the HTTP response into, based on the schema hash embedded in the envelope.
 */
export function extractEnvelopeWithHash(data: Uint8Array): EnvelopeContents | null {
  if (!isEnvelope(data)) {
    return null;
  }
  const payload = extractEnvelopePayload(data);
  if (!payload) {
    return null;
  }
  // Schema hash lives at bytes 6..38 (32 bytes of SHA-256).
  const hashBytes = data.subarray(6, 38);
  const schemaHash = bytesToHex(hashBytes);
  return { payload, schemaHash };
}

/**
 * Wraps a FlatBuffer payload in an LN2E envelope for outbound HTTP requests.
 *
 * @param payload  The serialized FlatBuffer bytes (from `builder.asUint8Array()`).
 * @param schemaHash  The SHA-256 hash of the .bfbs schema as a 64-char hex string.
 * @returns A new `Uint8Array` containing the 42-byte header + payload.
 */
export function wrapEnvelope(payload: Uint8Array, schemaHash: string): Uint8Array {
  const result = new Uint8Array(ENVELOPE_HEADER_SIZE + payload.length);
  // Magic: "LN2E"
  result[0] = ENVELOPE_MAGIC_0;
  result[1] = ENVELOPE_MAGIC_1;
  result[2] = ENVELOPE_MAGIC_2;
  result[3] = ENVELOPE_MAGIC_3;
  // Version
  result[4] = 0x01;
  // Hash algorithm: SHA-256
  result[5] = 0x01;
  // Schema hash (32 bytes)
  const hashBytes = hexToBytes(schemaHash);
  result.set(hashBytes, 6);
  // Payload length (4 bytes, little-endian)
  const view = new DataView(result.buffer);
  view.setUint32(38, payload.length, /* littleEndian= */ true);
  // Payload
  result.set(payload, ENVELOPE_HEADER_SIZE);
  return result;
}

/** Converts a Uint8Array to a lowercase hex string. */
function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

/** Converts a hex string to a Uint8Array. */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
